/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useState, useMemo, useCallback, memo, useEffect } from "react";
import { ColumnType, Props } from "./types";
import { Table as AntdTable, Empty, TableProps, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faThumbTack } from "@fortawesome/free-solid-svg-icons";
import { StyledThContainer } from "./index.styles";

const Table: FC<Props> = ({
  items,
  moneyThousandsSeparator = ".",
  moneyDecimalSeparator = ",",
  limitedHeight = true,
  sortedColumns,
  showFixingColumnIcon = true,
  emptyDescription,
}) => {
  const [fixedColumns, setFixedColumns] = useState<string[]>([]);

  useEffect(() => {
    setFixedColumns([]);
  }, [sortedColumns]);

  const isFixed = useCallback(
    (item: string) => fixedColumns.some((column) => column === item),
    [fixedColumns]
  );

  const onClickFaTumbTack = useCallback(
    (item: string) => {
      setFixedColumns((prevState) =>
        isFixed(item)
          ? prevState.filter((s) => s !== item)
          : [...prevState, item]
      );
    },
    [isFixed]
  );

  const setFaThumbTackColor = useCallback(
    (item: string) => (isFixed(item) ? "#1677FF" : "#969696"),
    [isFixed]
  );

  const setMoneyRegex = useMemo(() => {
    const regexPattern = `^\\d{1,3}(?:\\${moneyThousandsSeparator}\\d{3})*(?:\\${moneyDecimalSeparator}\\d{2})?$`;
    return new RegExp(regexPattern);
  }, [moneyThousandsSeparator, moneyDecimalSeparator]);

  const getColumnType = useCallback(
    (columnName: string) => {
      let type = ColumnType.other;

      for (let index = 0; index < items.length; index++) {
        const value = items[index][columnName];

        if (value === "") continue;

        if (isNaN(value) && typeof value === "string") {
          if (!isNaN(new Date(value as string).getTime())) {
            type = ColumnType.date;
          } else if (setMoneyRegex.test(value)) {
            type = ColumnType.money;
          } else {
            type = ColumnType.string;
          }
        } else if (!isNaN(value) || typeof value === "number") {
          type = ColumnType.number;
        }
        break;
      }

      return type;
    },
    [items, setMoneyRegex]
  );

  const setFilterOptions = useCallback(
    (columnName: string) => {
      const output: any = {};
      const type = getColumnType(columnName);

      if (type === ColumnType.string) {
        const filterOptions: any[] = [];

        items.forEach((x) => {
          const value = x[columnName];
          if (value && !filterOptions.includes(value)) {
            filterOptions.push(value);
          }
        });

        output.filters = filterOptions.map((option) => ({
          text: option,
          value: option,
        }));

        output.filterMode = "tree";

        output.filterSearch = true;

        output.onFilter = (value: string, record: any) =>
          record[columnName].startsWith(value);
      }

      return output;
    },
    [getColumnType, items]
  );

  const setSortFunc = useCallback(
    (columnName: string, a: any, b: any) => {
      const type = getColumnType(columnName);
      let output: any = true;

      if (type === ColumnType.string) {
        output = a[columnName].localeCompare(b[columnName]);
      } else if (type === ColumnType.number) {
        output = a[columnName] - b[columnName];
      } else if (type === ColumnType.money) {
        output =
          Number(
            (a[columnName] as string)
              .split(moneyDecimalSeparator)[0]
              .replace(new RegExp(`\\${moneyThousandsSeparator}`, "g"), "")
          ) -
          Number(
            (b[columnName] as string)
              .split(moneyDecimalSeparator)[0]
              .replace(new RegExp(`\\${moneyThousandsSeparator}`, "g"), "")
          );
      } else if (type === ColumnType.date) {
        output =
          new Date(a[columnName]).getTime() - new Date(b[columnName]).getTime();
      }

      return output;
    },
    [getColumnType, moneyDecimalSeparator, moneyThousandsSeparator]
  );

  const setColumnWidth = useCallback(
    (columnName: string) => `${columnName.length * 10 + 60}px`,
    []
  );

  const setColumnTextAlignment = useCallback(
    (columnName: string) =>
      getColumnType(columnName) === ColumnType.number ||
      getColumnType(columnName) === ColumnType.money
        ? "right"
        : "left",
    [getColumnType]
  );

  const getColumnNames = useCallback(
    () => sortedColumns || Object.keys(items[0] ?? {}),
    [sortedColumns, items]
  );

  const setColumns = useMemo(
    (): TableProps<(typeof items)[0]>["columns"] =>
      [
        ...fixedColumns,
        ...getColumnNames().filter(
          (column) => !fixedColumns.some((x) => x === column)
        ),
      ].map((item) => ({
        title: (
          <StyledThContainer className="th-container">
            <span>{item}</span>
            {showFixingColumnIcon && (
              <span
                style={{ cursor: "pointer", marginInline: "2px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onClickFaTumbTack(item);
                }}
              >
                <FontAwesomeIcon
                  icon={faThumbTack}
                  size="sm"
                  color={setFaThumbTackColor(item)}
                />
              </span>
            )}
          </StyledThContainer>
        ),
        dataIndex: item,
        key: item,
        fixed: isFixed(item),
        width: setColumnWidth(item),
        sorter: (a, b) => setSortFunc(item, a, b),
        showSorterTooltip: false,
        render: (value: any) => (
          <Tooltip title={String(value)} placement="right">
            <div
              style={{
                width: "100%",
                textAlign: setColumnTextAlignment(item),
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {String(value)}
            </div>
          </Tooltip>
        ),
        ...setFilterOptions(item),
      })),
    [
      fixedColumns,
      getColumnNames,
      isFixed,
      onClickFaTumbTack,
      setFaThumbTackColor,
      setColumnWidth,
      setSortFunc,
      setColumnTextAlignment,
      setFilterOptions,
      showFixingColumnIcon,
    ]
  );

  if (!items.length) {
    return emptyDescription ? <Empty description={emptyDescription} /> : <Empty />;
  }

  return (
    <div>
      <AntdTable
        pagination={{ position: ["bottomRight"], showSizeChanger: true }}
        columns={setColumns}
        dataSource={items}
        scroll={{ x: "100%", y: limitedHeight ? "50vh" : undefined }}
        components={{
          body: {
            row: ({ children, ...rest }: any) => <tr {...rest}>{children}</tr>,
            cell: ({ children, ...rest }: any) => <td {...rest}>{children}</td>,
          },
        }}
      />
    </div>
  );
};

export default memo(Table);
